import React, { FormEvent, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  InputTextField,
  Button,
  AutoCompleteField,
} from '@/components';
import { useTranslation } from 'react-i18next';
import useToastNotification from '@/hooks/useToastNotification';
import { useMutation, useQuery } from '@tanstack/react-query';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Divider } from 'primereact/divider';
import { getCitiesList } from '@/services/getCities';
import { getDepartmentList } from '@/services/getDepartments';
import {
  AutoCompleteCompleteEvent,
  AutoCompleteChangeEvent,
} from 'primereact/autocomplete';

import styles from './CustomerRegistration.module.scss';
import { registerCustomer } from './registerCustomer';
import { verifyRut } from './verifyRut';
import { titularRut } from './titularRut';

export interface CustomerRegistrationFormState {
  razonSocial: string;
  nombreFantasia: string;
  natFiscal: string;
  calle: string;
  calleNumero: string;
  departamento: string;
  ciudad: string;
  apto: string;
  email: string;
}

export interface CustomerVerificationFormState {
  rut: string;
}

interface CustomerRegistrationFormErrors {
  [key: string]: string;
}

const CustomerRegistration: React.FC = () => {
  const { t } = useTranslation('customerRegistration');
  const { showToast } = useToastNotification();
  const [searchParams] = useSearchParams();

  const {
    mutate,
    isError,
    isSuccess,
    isPending,
    data: registerCustomerResponse,
  } = useMutation({
    mutationFn: registerCustomer,
    onSuccess: () => {
      const from = searchParams.get('from');
      if (from) window.history.back();
    },
  });

  const [filteredCities, setFilteredCities] = useState<string[] | undefined>(
    []
  );

  const [filteredDepartments, setFilteredDepartments] = useState<
    string[] | undefined
  >([]);

  const [formRutData, setFormRutData] = useState<CustomerVerificationFormState>(
    { rut: '' }
  );

  const [initialState, setInitialState] = useState(false);
  const [showData, setShowData] = useState(false);

  const {
    mutate: mutateVerifyRut,
    isError: isErrorRut,
    isPending: rutLoading,
    data: rutFieldFormData,
  } = useMutation({
    mutationFn: verifyRut,
  });

  const { mutate: mutateTitularRut, isPending: rutTitularLoading } =
    useMutation({
      mutationFn: titularRut,
      onSuccess: (response) => {
        if (Object.keys(response).length) {
          showToast({
            severity: 'error',
            detail: t('titularExists'),
          });
        }
        if (!Object.keys(response).length) {
          mutateVerifyRut({ rut: formRutData.rut });
        }
      },
      onError: () => {
        mutateVerifyRut({ rut: formRutData.rut });
      },
    });

  const { data: citiesList, isLoading: citiesLoading } = useQuery({
    queryKey: ['citiesDropdown'],
    queryFn: getCitiesList,
  });

  const { data: departmentsList, isLoading: departmentsLoading } = useQuery({
    queryKey: ['departmentsDropdown'],
    queryFn: getDepartmentList,
  });

  const [formData, setFormData] = useState<CustomerRegistrationFormState>({
    razonSocial: '',
    nombreFantasia: '',
    natFiscal: '',
    calle: '',
    calleNumero: '',
    departamento: '',
    ciudad: '',
    apto: '',
    email: '',
  });

  const [formErrors, setFormErrors] = useState<CustomerRegistrationFormErrors>(
    {}
  );

  const citiesDropdown = citiesList?.map((city) => ({
    name: city,
    value: city,
  }));

  const departamentoDropdown = departmentsList?.map((city) => ({
    name: city,
    value: city,
  }));

  const searchCity = (event: AutoCompleteCompleteEvent) => {
    const filteredNames = citiesDropdown
      ?.map((item) => item.name)
      .filter((name) => name.toLowerCase().includes(event.query.toLowerCase()));

    setFilteredCities(filteredNames);
  };

  const searchDepartment = (event: AutoCompleteCompleteEvent) => {
    const filteredResults = departamentoDropdown
      ?.map((item) => item.name)
      .filter((name) => name.toLowerCase().includes(event.query.toLowerCase()));

    setFilteredDepartments(filteredResults);
  };

  const [formRutErrors, setFormRutErrors] =
    useState<CustomerRegistrationFormErrors>({});

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    name: string
  ) => {
    const { value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    setFormRutData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
    setFormRutErrors((prevErrors) => ({
      ...prevErrors,
      rut: '',
    }));
  };

  const handleAutoCompleteChange = (
    e: AutoCompleteChangeEvent,
    name: string
  ) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: e.value,
    }));
    setFormRutData((prevState) => ({
      ...prevState,
      [name]: e.value,
    }));
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
    setFormRutErrors((prevErrors) => ({
      ...prevErrors,
      rut: '',
    }));
  };

  const validateForm = (): boolean => {
    let errors: CustomerRegistrationFormErrors = { ...formErrors };
    const emailRegex = /\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/;
    if (!formData.razonSocial) {
      errors = {
        ...errors,
        razonSocial: t('razonSocialRequired'),
      };
    }
    if (!formData.nombreFantasia) {
      errors = { ...errors, nombreFantasia: t('nombreFantasiaRequired') };
    }
    if (!formData.natFiscal) {
      errors = { ...errors, natFiscal: t('natFiscalRequired') };
    }
    if (!formData.calle) {
      errors = {
        ...errors,
        calle: t('calleRequired'),
      };
    }
    if (!formData.calleNumero) {
      errors = { ...errors, calleNumero: t('calleNumeroRequired') };
    }
    if (!formData.departamento) {
      errors = {
        ...errors,
        departamento: t('departamentoRequired'),
      };
    }
    if (!formData.ciudad) {
      errors = {
        ...errors,
        ciudad: t('ciudadRequired'),
      };
    }

    if (formData.email && !emailRegex.test(formData.email)) {
      errors = {
        ...errors,
        email: t('emailFormatError'),
      };
    }

    setFormErrors(errors);
    return Object.values(errors).every((x) => x === '');
  };

  const validateRut = (): boolean => {
    let errors: CustomerRegistrationFormErrors = { ...formRutErrors };

    if (!formRutData.rut) {
      errors = { ...errors, rut: t('rutRequired') };
    }

    if (formRutData.rut && !/^\d{12}$/.test(formRutData.rut)) {
      errors = { ...errors, rut: t('rut12numbers') };
    }

    setFormRutErrors(errors);
    return Object.values(errors).every((x) => x === '');
  };

  useEffect(() => {
    if (isError || isErrorRut) {
      showToast({
        severity: 'error',
        detail: t('unexpectedError'),
      });
    }
  }, [isError, t, showToast, isErrorRut]);

  useEffect(() => {
    if (isSuccess) {
      if (
        registerCustomerResponse?.m_Item2 &&
        registerCustomerResponse?.m_Item1 === false
      ) {
        setInitialState(true);
        setFormRutData({
          rut: '',
        });
        try {
          const errorDetails = JSON.parse(registerCustomerResponse.m_Item2);
          const errorMessage = errorDetails?.error?.message?.value;

          if (errorMessage) {
            showToast({
              severity: 'error',
              detail: errorMessage,
            });
          } else {
            showToast({
              severity: 'error',
              detail: t('unexpectedError'),
            });
          }
        } catch (error) {
          showToast({
            severity: 'error',
            detail: t('unexpectedError'),
          });
        }
      } else {
        showToast({
          severity: 'success',
          detail: t('successfulRegistration'),
        });
        setInitialState(true);

        setFormRutData({
          rut: '',
        });
      }
    }
  }, [isSuccess, registerCustomerResponse, t, showToast]);

  useEffect(() => {
    if (rutFieldFormData?.m_Item1) {
      setFormData({
        razonSocial: rutFieldFormData?.m_Item1?.razonSocial,
        nombreFantasia: rutFieldFormData?.m_Item1?.nombreFantasia,
        natFiscal: rutFieldFormData?.m_Item1?.naturalezaFiscal,
        calle: rutFieldFormData?.m_Item1?.calle,
        calleNumero: rutFieldFormData?.m_Item1?.calleNro,
        departamento: rutFieldFormData?.m_Item1?.departamento,
        ciudad: rutFieldFormData?.m_Item1?.ciudad,
        apto: `${rutFieldFormData?.m_Item1?.aptoNro}`,
        email: '',
      });
    }
    if (rutFieldFormData?.m_Item1 === null && rutFieldFormData?.m_Item2) {
      showToast({
        severity: 'error',
        detail: rutFieldFormData?.m_Item2,
      });
    }
  }, [rutFieldFormData, showToast]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    validateForm();
    const data = { ...formData, ...formRutData };
    if (validateForm()) {
      mutate(data);
    }
  };

  const handleRUTSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formRutData.rut.trim() === '' || !validateRut()) {
      setFormData({
        razonSocial: '',
        nombreFantasia: '',
        natFiscal: '',
        calle: '',
        calleNumero: '',
        departamento: '',
        ciudad: '',
        apto: '',
        email: '',
      });
      setShowData(false);
      setFormRutErrors({});
      setFormErrors({});
      validateRut();
      return;
    }
    setShowData(true);
    validateRut();
    setInitialState(false);
    if (validateRut()) {
      mutateTitularRut({ rut: formRutData.rut });
    }
  };

  return (
    <Container
      title={t('customerRegistration')}
      className={styles.containerCustomer}
    >
      <div className="container mx-100">
        <form onSubmit={handleRUTSubmit} className="mb-4">
          <div className="flex flex-column md:flex-row justify-content-start align-items-center">
            <div className="col-12 md:col-3">
              <InputTextField
                label={t('rut')}
                id="rut"
                name="rut"
                value={formRutData.rut}
                onChange={(e) => handleInputChange(e, 'rut')}
                error={formRutErrors['rut']}
                placeholder={t('rutPlaceholder')}
                required
              />
            </div>
            <div className="col-12 md:col-2">
              <Button
                label={t('verifyTaxId')}
                type="submit"
                secondary
                loading={rutLoading || rutTitularLoading}
                className={styles.verifyRut}
              />
            </div>
          </div>
        </form>
        {rutFieldFormData?.m_Item1 !== null &&
          rutFieldFormData?.m_Item1 &&
          !initialState &&
          !rutLoading &&
          !rutTitularLoading &&
          showData && (
            <form onSubmit={handleSubmit} className="mb-4" action="#">
              <h3 className={`${styles.subTitleOne} ml-2`}>
                {t('companyData')}
              </h3>

              <div className="flex flex-column md:flex-row justify-content-start align-items-center">
                <div className="md:col-4">
                  <InputTextField
                    label={t('razonSocial')}
                    id="razonSocial"
                    name="razonSocial"
                    value={formData.razonSocial}
                    onChange={(e) => handleInputChange(e, 'razonSocial')}
                    error={formErrors['razonSocial']}
                    placeholder={t('razonSocialPlaceholder')}
                    required
                    disabled={!!rutFieldFormData?.m_Item1?.razonSocial}
                  />
                </div>
                <div className="md:col-4">
                  <InputTextField
                    label={t('nombreFantasia')}
                    id="nombreFantasia"
                    name="nombreFantasia"
                    value={formData.nombreFantasia}
                    onChange={(e) => handleInputChange(e, 'nombreFantasia')}
                    error={formErrors['nombreFantasia']}
                    placeholder={t('nombreFantasiaPlaceholder')}
                    required
                    disabled={!!rutFieldFormData?.m_Item1?.nombreFantasia}
                  />
                </div>
                <div className="md:col-4">
                  <InputTextField
                    label={t('natFiscal')}
                    id="natFiscal"
                    name="natFiscal"
                    value={formData.natFiscal}
                    onChange={(e) => handleInputChange(e, 'natFiscal')}
                    error={formErrors['natFiscal']}
                    placeholder={t('natFiscalPlaceholder')}
                    required
                    disabled={!!rutFieldFormData?.m_Item1?.naturalezaFiscal}
                  />
                </div>
              </div>

              <h3 className={`${styles.subTitleOne} ml-2`}>{t('address')}</h3>

              <div className="flex flex-column md:flex-row justify-content-start align-items-center">
                <div className="md:col-4">
                  <InputTextField
                    label={t('calle')}
                    id="calle"
                    name="calle"
                    value={formData.calle}
                    onChange={(e) => handleInputChange(e, 'calle')}
                    error={formErrors['calle']}
                    placeholder={t('callePlaceholder')}
                    required
                    disabled={!!rutFieldFormData?.m_Item1?.calle}
                  />
                </div>
                <div className="md:col-4">
                  <InputTextField
                    label={t('calleNumero')}
                    id="calleNumero"
                    name="calleNumero"
                    value={formData.calleNumero}
                    onChange={(e) => handleInputChange(e, 'calleNumero')}
                    error={formErrors['calleNumero']}
                    placeholder={t('calleNumeroPlaceholder')}
                    disabled={!!rutFieldFormData?.m_Item1?.calleNro}
                    required
                  />
                </div>
                <div className="md:col-4">
                  <InputTextField
                    label={t('apto')}
                    id="apto"
                    name="apto"
                    value={formData.apto}
                    onChange={(e) => handleInputChange(e, 'apto')}
                    error={formErrors['apto']}
                    placeholder={t('aptoPlaceholder')}
                    disabled={!!rutFieldFormData?.m_Item1?.aptoNro}
                  />
                </div>
              </div>

              <div className="flex flex-column md:flex-row justify-content-start align-items-center">
                <div className="md:col-4">
                  <AutoCompleteField
                    placeholder={t('selectDepartment')}
                    label={t('departamento')}
                    value={formData.departamento}
                    suggestions={filteredDepartments}
                    completeMethod={searchDepartment}
                    field="departamento"
                    id="departamento"
                    onChange={(e) =>
                      handleAutoCompleteChange(e, 'departamento')
                    }
                    required
                    dropdown
                    isLoading={departmentsLoading}
                    disabled={!!rutFieldFormData?.m_Item1?.departamento}
                  />
                </div>
                <div className="md:col-4">
                  <AutoCompleteField
                    placeholder={t('selectCity')}
                    label={t('city')}
                    value={formData.ciudad}
                    suggestions={filteredCities}
                    completeMethod={searchCity}
                    field="ciudad"
                    id="ciudad"
                    onChange={(e) => handleAutoCompleteChange(e, 'ciudad')}
                    required
                    dropdown
                    isLoading={citiesLoading}
                    disabled={!!rutFieldFormData?.m_Item1?.ciudad}
                  />
                </div>
                <div className="md:col-4" />
              </div>

              <h3 className={`${styles.subTitleOne} ml-2 mt-4`}>
                {t('contactData')}
              </h3>
              <div className="flex flex-column md:flex-row justify-content-start align-items-center">
                <div className="md:col-4">
                  <InputTextField
                    label={t('email')}
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange(e, 'email')}
                    error={formErrors['email']}
                    placeholder={t('emailPlaceholder')}
                  />
                </div>
              </div>
              <Divider className={styles.divider} />
              <div className="flex justify-content-between flex-column md:flex-row align-items-center">
                <p className={styles.disclaimer}>{t('mandatoryFields')}</p>
                <Button
                  type="submit"
                  green
                  className="md:col-2"
                  loading={isPending}
                >
                  <FontAwesomeIcon icon={faCheck} className="mx-3" />
                  {t('createClientButton')}
                </Button>
              </div>
            </form>
          )}
      </div>
    </Container>
  );
};

export default CustomerRegistration;
